import { useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type Infraccion = {
  id: number;
  nro_acta: string;
  patente: string;
  tipo_descripcion: string;
  tipo_codigo: string;
  monto_centavos: number;
  monto_final_centavos: number;
  estado: 'pendiente' | 'pagada' | 'anulada';
  permite_pronto_pago: boolean;
  descuento_pronto_pago_porc: number;
  dias_pronto_pago: number;
  pronto_pago_vence_en: string | null;
  fecha_infraccion: string;
  direccion: string | null;
  municipio_nombre: string;
};

function fmtFecha(s: string): string {
  return new Date(s.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function diasRestantes(vence: string): number {
  return Math.max(0, Math.ceil((new Date(vence.replace(' ', 'T')).getTime() - Date.now()) / 86400000));
}

export default function Infracciones() {
  const [lista, setLista]     = useState<Infraccion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [pagando, setPagando] = useState<number | null>(null);
  const [err, setErr]         = useState<string | null>(null);
  const [msg, setMsg]         = useState<string | null>(null);

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    try {
      const data = await api<Infraccion[]>('/infracciones?action=mis_infracciones');
      setLista(data);
    } catch (e: any) {
      setErr(e.message || 'Error cargando infracciones');
    } finally {
      setCargando(false);
    }
  }

  async function pagar(infrId: number) {
    const infr = lista.find((i) => i.id === infrId);
    if (!infr) return;
    const monto = `$${(infr.monto_final_centavos / 100).toFixed(2)}`;
    if (!confirm(`¿Pagar la infracción ${infr.nro_acta} por ${monto}?\n\nEl saldo se deducirá de tu billetera.`)) return;
    setPagando(infrId); setErr(null); setMsg(null);
    try {
      await api('/infracciones?action=pagar', {
        method: 'POST',
        body: JSON.stringify({ infraccion_id: infrId }),
      });
      setMsg(`Infracción ${infr.nro_acta} pagada.`);
      await cargar();
    } catch (e: any) {
      setErr(e.message || 'Error al pagar');
    } finally {
      setPagando(null);
    }
  }

  const pendientes = lista.filter((i) => i.estado === 'pendiente');
  const historial  = lista.filter((i) => i.estado !== 'pendiente');

  return (
    <>
      <TopBar title="Mis infracciones" back />
      <main className="px-4 py-5 space-y-4 max-w-md mx-auto pb-24">
        {cargando && <p className="text-gray-400 text-sm text-center pt-10">Cargando…</p>}

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
        )}
        {msg && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3">{msg}</div>
        )}

        {!cargando && lista.length === 0 && (
          <div className="text-center py-12">
            <p className="text-4xl mb-3">✅</p>
            <p className="text-gray-800 font-semibold">Sin infracciones</p>
            <p className="text-gray-500 text-sm mt-1">No tenés infracciones registradas.</p>
          </div>
        )}

        {/* Pendientes */}
        {pendientes.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pendientes de pago</p>
            {pendientes.map((infr) => {
              const isPp =
                infr.permite_pronto_pago &&
                infr.pronto_pago_vence_en &&
                diasRestantes(infr.pronto_pago_vence_en) > 0;
              const dias = isPp ? diasRestantes(infr.pronto_pago_vence_en!) : 0;

              return (
                <div
                  key={infr.id}
                  className="bg-white border border-red-200 rounded-2xl p-4 space-y-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-lg font-mono text-gray-900">{infr.nro_acta}</p>
                      <p className="text-sm text-gray-700">{infr.tipo_descripcion}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {infr.patente} · {fmtFecha(infr.fecha_infraccion)}
                      </p>
                      {infr.direccion && (
                        <p className="text-xs text-gray-400">{infr.direccion}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {isPp ? (
                        <>
                          <p className="text-xs text-green-600 font-semibold">
                            -{infr.descuento_pronto_pago_porc}% pronto pago
                          </p>
                          <p className="text-xs text-gray-400 line-through">
                            ${(infr.monto_centavos / 100).toFixed(2)}
                          </p>
                        </>
                      ) : null}
                      <p className="text-xl font-extrabold text-gray-900">
                        ${(infr.monto_final_centavos / 100).toFixed(2)}
                      </p>
                      {isPp && (
                        <p className="text-xs text-amber-600 font-medium">{dias} días restantes</p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => pagar(infr.id)}
                    disabled={pagando === infr.id}
                    className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm"
                  >
                    {pagando === infr.id ? 'Pagando…' : 'Pagar con billetera'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Historial */}
        {historial.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Historial</p>
            {historial.map((infr) => (
              <div
                key={infr.id}
                className="bg-white border border-app rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm"
              >
                <span className="text-xl flex-shrink-0">
                  {infr.estado === 'pagada' ? '✅' : '🚫'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-mono font-semibold text-sm text-gray-900">{infr.nro_acta}</p>
                  <p className="text-xs text-gray-500 truncate">{infr.tipo_descripcion}</p>
                  <p className="text-xs text-gray-400">{fmtFecha(infr.fecha_infraccion)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                  infr.estado === 'pagada'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {infr.estado}
                </span>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
